import { Controller } from "@hotwired/stimulus"

// Submits the form when a choice changes, so a preview updates as you pick
// rather than after you press Continue.
//
// The recalculation happens on the SERVER: the same pure calculators produce
// the stored result, so a preview can never promise a number the saved goal
// will not hold. That is worth a round trip.
export default class extends Controller {
  static targets = ["preview"]

  // How long to wait after the last keystroke, and how few characters are
  // worth searching for. Both zero by default, so a dropdown keeps
  // submitting the instant it changes — a debounce on a <select> is just a
  // delay.
  //
  // 300ms is the usual resting point: long enough that an average typist
  // finishes a word first, short enough not to feel laggy. Two characters
  // because one letter matches most of a 7,959-row library, which is a
  // slow query returning a useless answer.
  static values = { delay: Number, minLength: Number }

  disconnect() {
    clearTimeout(this.timer)
  }

  submit() {
    // Marks this as a preview so the server re-renders the step instead of
    // advancing the wizard — the Continue button leaves the field empty.
    if (this.hasPreviewTarget) this.previewTarget.value = "1"

    // requestSubmit, not submit(): it fires the submit event, which is what
    // Turbo listens for. Calling .submit() bypasses Turbo and navigates.
    this.element.requestSubmit()
  }

  // Typing. Waits for a pause rather than firing per keystroke, which would
  // be one request per letter and a race between their responses.
  search(event) {
    clearTimeout(this.timer)

    const value = event.target.value.trim()

    // An empty box means "show me everything again", so it always submits —
    // otherwise clearing the field would leave the last results on screen
    // with nothing explaining why.
    if (value.length > 0 && value.length < this.minLengthValue) return

    this.timer = setTimeout(() => this.submit(), this.delayValue || 300)
  }

  // Continue must advance, so the preview flag is cleared on a real submit.
  advance() {
    if (this.hasPreviewTarget) this.previewTarget.value = ""
  }
}
